import type * as FaIcons from "react-icons/fa";

import { type IconSetKey, iconSets } from "../DynamicIcon";
import React, { useMemo, useState } from "react";
import { ImagePlus, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { authenticatedBitswanBackendInstance } from "@/lib/api-client";

interface IconInfo {
  name: string;
  component: React.ComponentType<any>;
}

type IconSelectorProps = {
  onSelectIcon: (icon: string) => void;
  value?: string;
};

const SUGGESTED = ["FaGoogle", "FaSlack", "FaGithub", "FaCloud", "FaChartBar", "FaShoppingCart", "FaWarehouse", "FaFileInvoice", "FaCalendarAlt", "FaEnvelope", "FaDatabase", "FaTruck"];
const isUploaded = (value?: string) => Boolean(value && /^(https?:\/\/|\/|data:image\/)/.test(value));

export const IconSelector = ({ onSelectIcon, value }: IconSelectorProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [uploading, setUploading] = useState(false);

  const allIcons = useMemo<IconInfo[]>(() => {
    return (Object.entries(iconSets) as [IconSetKey, typeof FaIcons][]).flatMap(
      ([, iconSet]) =>
        Object.entries(iconSet).map(([iconName, component]) => ({
          name: iconName,
          component: component as React.ComponentType<any>,
        })),
    );
  }, []);

  const shown = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) {
      const byName = new Map(allIcons.map((icon) => [icon.name, icon]));
      return SUGGESTED.map((name) => byName.get(name)).filter(Boolean) as IconInfo[];
    }
    return allIcons.filter((icon) => icon.name.toLowerCase().includes(needle)).slice(0, 36);
  }, [allIcons, searchTerm]);

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const api = await authenticatedBitswanBackendInstance();
      const form = new FormData();
      form.append("image", file);
      const res = await api.post("/advisor/content-images/", form, { headers: { "Content-Type": "multipart/form-data" } });
      onSelectIcon(res.data.url);
    } catch (error: any) {
      toast.error(error?.response?.data?.error ?? "Could not upload that image.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Tabs defaultValue={isUploaded(value) ? "upload" : "library"} className="w-full">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="library" data-testid="icon-tab-library">Built-in icon</TabsTrigger>
        <TabsTrigger value="upload" data-testid="icon-tab-upload">Your own image</TabsTrigger>
      </TabsList>
      <TabsContent value="library" className="space-y-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search icons..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
            data-testid="icon-search"
          />
        </div>
        <div className="grid grid-cols-6 gap-2" data-testid="icon-grid">
          {shown.map((icon) => {
            const selected = value === icon.name;
            return (
              <button
                type="button"
                key={icon.name}
                onClick={() => onSelectIcon(icon.name)}
                title={icon.name}
                aria-pressed={selected}
                className={`flex h-11 items-center justify-center rounded-lg border transition-colors ${
                  selected ? "border-primary bg-primary/10 text-primary" : "text-muted-foreground hover:border-foreground/30 hover:bg-muted"
                }`}
                data-testid={`icon-option-${icon.name}`}
              >
                <icon.component size={18} />
              </button>
            );
          })}
          {shown.length === 0 && (
            <p className="col-span-6 py-4 text-center text-sm text-muted-foreground">No icon matches that.</p>
          )}
        </div>
      </TabsContent>
      <TabsContent value="upload">
        <label
          className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground hover:border-foreground/40 hover:bg-muted/40"
          data-testid="icon-upload"
        >
          {uploading ? <Loader2 className="h-6 w-6 animate-spin" /> : isUploaded(value) ? (
            <img src={value} alt="" className="h-12 w-12 object-contain" />
          ) : (
            <ImagePlus className="h-6 w-6" />
          )}
          <span>{isUploaded(value) ? "Choose a different image" : "Choose an image"}</span>
          <span className="text-xs">PNG, JPEG, WebP or GIF, up to 5 MB. Square images look best.</span>
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid="icon-upload-input"
          />
        </label>
      </TabsContent>
    </Tabs>
  );
};
